import type { ReactNode } from 'react';
import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Button,
    Card,
    Col,
    Form,
    FormButtonRow,
    FormGroup,
    FormParagraph,
    FormSection,
    IconButton,
    Row,
    Text,
} from '@ksyos/design-system';
import { Fragment, useEffect, useState } from 'react';
import type { IAbsence } from '@frontend/components/interfaces/absence';
import { absenceAddEditEndDateTodayOrInFutureSchema, absenceAddEditSchema } from '@common/validation/absence';
import { validateDatetimeOverlap } from '@common/validation/utils/validateDatetimeOverlap';
import { DateInput } from '@frontend/components/elements/common';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { FormattedDate } from '@frontend/components/userDataManagement/FormattedDate';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { translateKey } from '@frontend/translate';
import { emptyValidationErrors, useAbortableFetch, validate } from '@frontend/utils/';

export interface IAbsencesProps {
    practitionerId: string;
}

interface IAbsenceWithAuthorization extends IAbsence {
    authorization?: {
        canDelete: boolean;
        canUpdate: boolean;
    };
}

export function Absence(props: IAbsencesProps) {
    const abortableFetch = useAbortableFetch();
    const [originalAbsences, setOriginalAbsences] = useState<IAbsenceWithAuthorization[]>([]);
    const [absences, setAbsences] = useState<IAbsenceWithAuthorization[]>([]);
    const [canAdd, setCanAdd] = useState(false);
    const [canEditOrRemove, setCanEditOrRemove] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        load();
    }, [props.practitionerId]);

    if (!absences) {
        return null;
    }

    return (
        <Card>
            <Card.Transition type={(canEditOrRemove || canAdd) && isEditing ? 'edit' : 'read'} />
            <Card.Header
                action={
                    (canEditOrRemove || canAdd) && !isEditing && absences.length !== 0 ? (
                        <IconButton
                            icon="edit-light"
                            labelText="edit"
                            onClick={() => setIsEditing(true)}
                        />
                    ) : undefined
                }
                title={translateKey('profile.absence')}
            />
            <Card.Body>
                <Form submitOnEnter={false}>
                    {!isEditing && renderAbsencesReadOnly()}

                    {isEditing && (
                        <>
                            {renderAbsencesEdit()}
                            {renderAddAbsence()}
                        </>
                    )}

                    {isEditing && (
                        <FormButtonRow>
                            <CancelButton
                                disabled={isSaving}
                                onCancel={async () => await handleCancel()}
                            />
                            <SaveButton
                                disabled={isSaving}
                                onSave={async () => await handleSubmit()}
                            />
                        </FormButtonRow>
                    )}
                </Form>
            </Card.Body>
        </Card>
    );

    function renderAbsencesReadOnly() {
        const items: ReactNode[] = [];
        for (const absence of absences) {
            if (absence.startDate === 'delete') {
                continue;
            } else if (absence.startDate && absence.endDate) {
                if (absence.startDate === absence.endDate) {
                    items.push(<FormattedDate value={absence.startDate} />);
                } else {
                    items.push(
                        <Row columnGap={2}>
                            <Col span={[5, null, 3]}>
                                <FormattedDate value={absence.startDate} />
                            </Col>
                            <Col span={2}>
                                <Text
                                    as="small"
                                    mt={[1, null, 2]}
                                    size="xs"
                                    textAlign="center"
                                >
                                    {translateKey('common.until')}
                                </Text>
                            </Col>
                            <Col span={[5, null, 3]}>
                                <FormattedDate value={absence.endDate} />
                            </Col>
                        </Row>
                    );
                }
            } else if (absence.startDate || absence.endDate) {
                items.push(<FormattedDate value={absence.startDate || absence.endDate} />);
            } else {
                items.push(<FormParagraph>-</FormParagraph>);
            }
        }

        if (items.length === 0) {
            return (
                <>
                    <Text mb={4}>{translateKey('profile.holidayAbsence')}</Text>
                    {renderAddAbsence()}
                </>
            );
        } else {
            return (
                <>
                    {items.map((item, i) => {
                        return (
                            <Fragment key={i}>
                                <FormGroup label={translateKey('common.period')}>{item}</FormGroup>
                            </Fragment>
                        );
                    })}
                </>
            );
        }
    }

    function renderAbsencesEdit() {
        return absences.map((absence, key) => {
            if (absence.change === 'delete') {
                return;
            } else {
                return (
                    <FormSection key={key}>
                        <FormGroup
                            key={key}
                            label={translateKey('common.period')}
                        >
                            <Row
                                columnGap={[2, null, 4]}
                                rowGap={2}
                            >
                                {!!absence.authorization?.canUpdate || absence.change === 'create' ? (
                                    <Col span={[12, 5]}>
                                        <DateInput
                                            error={absence.errors.get('startDate')}
                                            onChange={async (value) => await handleChange(value, 'startDate', key)}
                                            size="large"
                                            value={absence.startDate}
                                            variant="outline"
                                        />
                                    </Col>
                                ) : (
                                    <Col span={[12, 5]}>
                                        <FormattedDate value={absence.startDate} />
                                    </Col>
                                )}

                                <Col span={[12, 1]}>
                                    <Text
                                        as="small"
                                        mt={[0, 3]}
                                        size="xs"
                                        textAlign="center"
                                    >
                                        {translateKey('common.until')}
                                    </Text>
                                </Col>
                                {!!absence.authorization?.canUpdate || absence.change === 'create' ? (
                                    <Col span={[12, 5]}>
                                        <DateInput
                                            error={absence.errors.get('endDate')}
                                            onChange={async (value) => await handleChange(value, 'endDate', key)}
                                            size="large"
                                            value={absence.endDate}
                                            variant="outline"
                                        />
                                    </Col>
                                ) : (
                                    <Col span={[12, 5]}>
                                        <FormattedDate value={absence.endDate} />
                                    </Col>
                                )}

                                {!!absence.authorization?.canDelete && (
                                    <Col span={[12, 1]}>
                                        <Box
                                            display="flex"
                                            justifyContent={['center', 'flex-end']}
                                        >
                                            <IconButton
                                                icon="delete"
                                                onClick={() => onRemoveAbsence(key)}
                                            />
                                        </Box>
                                    </Col>
                                )}
                            </Row>
                        </FormGroup>
                    </FormSection>
                );
            }
        });
    }

    function renderAddAbsence() {
        if (canAdd) {
            return (
                <FormSection>
                    <Button
                        iconLeft="add"
                        onClick={async (_e) => {
                            setIsEditing(true);
                            await onAddAbsence();
                        }}
                        variant="link"
                    >
                        {translateKey('profile.addPeriod')}
                    </Button>
                </FormSection>
            );
        }

        return null;
    }

    async function handleChange(value: string | null, name: 'startDate' | 'endDate', index: number) {
        const newAbsences = [...absences];
        const newAbsence = { ...absences[index] };
        newAbsence[name] = value;
        newAbsence.errors = emptyValidationErrors;
        if (!newAbsence.change) {
            newAbsence.change = 'update';
        }
        newAbsences[index] = newAbsence;
        setAbsences(newAbsences);
    }

    async function load() {
        const url = `/api/practitioner/absences/?practitionerId=${props.practitionerId}&onlyActiveAbsences=true&includeListAuthorizations=canAdd&includeItemAuthorizations=canUpdate,canDelete`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        const items = result.json.items.map((item: any) => ({ ...item, errors: emptyValidationErrors }));

        setOriginalAbsences(items);
        setAbsences(items);
        setCanAdd(result.json.authorization.canAdd);
        setCanEditOrRemove(
            !!items.find(
                (item: IAbsenceWithAuthorization) => !!item.authorization?.canUpdate || !!item.authorization?.canDelete
            )
        );
    }

    async function onAddAbsence() {
        setAbsences([...absences, createNewAbsence()]);
    }

    async function onRemoveAbsence(index: number) {
        const newAbsences = [...absences];
        const newAbsence = { ...absences[index] };
        if (newAbsence.change === 'create') {
            newAbsences.splice(index, 1);
        } else {
            newAbsence.change = 'delete';
            newAbsences[index] = newAbsence;
        }

        setAbsences(newAbsences);
    }

    function createNewAbsence(): IAbsence {
        return {
            startDate: null,
            endDate: null,
            change: 'create',
            errors: emptyValidationErrors,
        };
    }

    async function handleCancel() {
        setAbsences(originalAbsences);
        setIsEditing(false);
    }

    async function handleSubmit() {
        setIsSaving(true);

        const newAbsences = [...absences];
        const deletedAbsences = newAbsences.filter((absence) => absence.change === 'delete');
        let absencesToCheck = newAbsences.filter((absence) => absence.change !== 'delete');

        absencesToCheck = validateAbsences(absencesToCheck);
        const hasErrors = absencesToCheck.some((absence) => absence.errors.some((error) => error !== undefined));

        if (hasErrors) {
            setAbsences([...absencesToCheck, ...deletedAbsences]);
        } else {
            for (const absence of absences) {
                if (absence.change === 'create') {
                    await createAbsence(absence);
                }
                if (absence.change === 'update') {
                    await updateAbsence(absence);
                }
                if (absence.change === 'delete') {
                    await removeAbsence(absence);
                }
            }
            await load();
            setIsEditing(false);
        }
        setIsSaving(false);
    }

    function validateAbsences(items: IAbsence[]) {
        const absencesToValidate = [...items];
        let hasErrors = false;
        for (const absence of absencesToValidate) {
            absence.errors = emptyValidationErrors;

            const validationResult = validateAbsence(absence);
            if (validationResult.hasErrors) {
                hasErrors = true;
                absence.errors = validationResult.errors;
            }
        }
        if (!hasErrors) {
            return validateDatetimeOverlap(absencesToValidate);
        }

        return absencesToValidate;
    }

    async function createAbsence(data: IAbsence) {
        const url = `/api/practitioner/absences?practitionerId=${props.practitionerId}`;
        const absence = {
            startDate: data.startDate,
            endDate: data.endDate,
        };
        await abortableFetch.fetchJson(url, 'POST', absence);
    }

    async function updateAbsence(data: IAbsence) {
        if (data.id) {
            let url = '/api/practitioner/absences/';
            url += data.id;
            const absence = {
                startDate: data.startDate,
                endDate: data.endDate,
            };
            await abortableFetch.fetchJson(url, 'POST', absence);
        }
    }

    async function removeAbsence(data: IAbsence) {
        if (data.id) {
            const url = '/api/practitioner/absences/' + data.id;
            await abortableFetch.fetchJson(url, 'DELETE');
        }
    }

    function validateAbsence(absence: { endDate: string | null; startDate: string | null }) {
        const yesterday = DateTime.now().subtract(1, 'day');
        const absenceValidationResult = validate(absenceAddEditSchema, absence);
        if (absenceValidationResult.hasErrors) {
            return absenceValidationResult;
        } else {
            return validate(absenceAddEditEndDateTodayOrInFutureSchema(yesterday.toDate()), {
                endDate: DateTime.parse(absence.endDate!, 'YYYY-MM-DD').toDate(),
            });
        }
    }
}
